import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import MyAccountPageWrapper from '../MyAccountPageWrapper';
import useTranslation from '../../../hooks/translation-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import NavigationElements from '../../../models/NavigationElements';
import { View } from 'react-native';
import Label, { LabelType } from '../../common/Label';
import CustomSwitch from '../../common/CustomSwitch';
import {
  GetNotificationSettingsDto,
  GetNotificationSettingsViewModel,
  MysteryShopperClient,
  MysteryShopperNotificationSubject,
  MysteryShopperNotificationType,
  SaveNotificationSettingsRequest,
} from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Loading from '../../common/Loading';
import useCustomTheme from '../../../hooks/theme-hook';

export interface SettingsNotificationsProps {
  noWrapper?: boolean;
  authToken?: string;
  onSaveSettings?(): void;
  onLoad?(): void;
}

type NotificationField = 'emailEnabled' | 'pushNotificationEnabled';

interface NotificationSettingRowProps {
  item: GetNotificationSettingsDto;
  emailLabel: string;
  mobileLabel: string;
  titleColor: string;
  emailDisabled: boolean;
  mobileDisabled: boolean;
  onEmailToggle(subject: MysteryShopperNotificationSubject | undefined): void;
  onMobileToggle(subject: MysteryShopperNotificationSubject | undefined): void;
}

const notificationRowStyle = { flexDirection: 'row', alignItems: 'center', gap: 16, paddingVertical: 6, marginBottom: 20 } as const;
const notificationToggleStyle = { flexDirection: 'row', alignItems: 'center', gap: 8 } as const;
const cloneNotificationSetting = (item: GetNotificationSettingsDto, patch?: Partial<GetNotificationSettingsDto>) =>
  Object.assign(new GetNotificationSettingsDto(), item, patch);
const updateNotificationCollection = (
  items: GetNotificationSettingsDto[] | undefined,
  subject: MysteryShopperNotificationSubject,
  field: NotificationField,
  enabled: boolean
) => {
  if (!items) {
    return items;
  }

  return items.map((item) => (item.subject === subject ? cloneNotificationSetting(item, { [field]: enabled }) : item));
};

const NotificationSettingRow = React.memo((props: NotificationSettingRowProps) => {
  const { item, titleColor, emailLabel, mobileLabel, emailDisabled, mobileDisabled, onEmailToggle, onMobileToggle } = props;
  const showMobileToggle =
    item.subject !== MysteryShopperNotificationSubject.WeeklyMissionDigest &&
    item.subject !== MysteryShopperNotificationSubject.ExclusiveMarketingEmail;

  return (
    <View>
      <Label variant={LabelType.titleSmall} style={{ color: titleColor }}>
        {item.subjectTitle}
      </Label>
      <View style={notificationRowStyle}>
        <View style={notificationToggleStyle}>
          <Label style={{ fontSize: 14 }}>{emailLabel}</Label>
          <CustomSwitch isEnabled={item.emailEnabled} disabled={emailDisabled} onPress={() => onEmailToggle(item.subject)} />
        </View>
        {showMobileToggle && (
          <View style={notificationToggleStyle}>
            <Label style={{ fontSize: 14 }}>{mobileLabel}</Label>
            <CustomSwitch isEnabled={item.pushNotificationEnabled} disabled={mobileDisabled} onPress={() => onMobileToggle(item.subject)} />
          </View>
        )}
      </View>
    </View>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.emailLabel === nextProps.emailLabel &&
    prevProps.mobileLabel === nextProps.mobileLabel &&
    prevProps.titleColor === nextProps.titleColor &&
    prevProps.emailDisabled === nextProps.emailDisabled &&
    prevProps.mobileDisabled === nextProps.mobileDisabled &&
    prevProps.onEmailToggle === nextProps.onEmailToggle &&
    prevProps.onMobileToggle === nextProps.onMobileToggle
  );
});

const getToggleKey = (subject: MysteryShopperNotificationSubject, type: MysteryShopperNotificationType) => `${subject}_${type}`;

const SettingsNotifications = (props: SettingsNotificationsProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const msApi = useApi(MysteryShopperClient);
  const queryClient = useQueryClient();
  const authStore = useAppSelector((selector) => selector.auth);
  const userPrefs = useAppSelector((selector) => selector.userPrefs);

  const [notifications, setNotifications] = useState<GetNotificationSettingsDto[]>();
  const [customToken, setCustomToken] = useState<string>();
  const [pendingToggleKeys, setPendingToggleKeys] = useState<Record<string, boolean>>({});
  const notificationsRef = useRef<GetNotificationSettingsDto[] | undefined>(undefined);
  const pendingToggleKeysRef = useRef<Record<string, boolean>>({});

  const saveNotificationSettingMutation = useMutation({
    mutationFn: (data: SaveNotificationSettingsRequest) => {
      return msApi.saveNotificationSettings(data, msApi.tokenSource?.token);
    },
  });

  useEffect(() => {
    if (saveNotificationSettingMutation.isSuccess) {
      props.onSaveSettings && props.onSaveSettings();
    }
  }, [saveNotificationSettingMutation.isSuccess, props.onSaveSettings]);

  useEffect(() => {
    setCustomToken(props.authToken);
  }, [props.authToken]);

  const notificationSettingsQueryKey = ['notifcationSettingsQuery', userPrefs.uiLanguage] as const;

  const notifcationSettingsQuery = useQuery({
    enabled: !!authStore.idToken || !!customToken,
    queryKey: notificationSettingsQueryKey,
    retry: customToken ? 0 : 3,
    queryFn: ({ signal }) => {
      if (customToken) {
        msApi.setCustomToken(customToken);
        msApi.setCustomCulture(userPrefs.uiLanguage);
      }

      signal?.addEventListener('abort', () => {
        msApi.tokenSource?.cancel('Query was cancelled by React Query');
        msApi.onCancel();
      });

      return msApi.getNotificationSettings(msApi.tokenSource?.token);
    },
  });

  useEffect(() => {
    if (notifcationSettingsQuery.isSuccess) {
      setNotifications(notifcationSettingsQuery.data.notificationSettings);
    }
  }, [notifcationSettingsQuery.isSuccess, notifcationSettingsQuery.data]);

  useFocusEffect(
    React.useCallback(() => {
      if (!authStore.idToken && !customToken) {
        return undefined;
      }

      void notifcationSettingsQuery.refetch();

      return undefined;
    }, [authStore.idToken, customToken, notifcationSettingsQuery.refetch])
  );

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    pendingToggleKeysRef.current = pendingToggleKeys;
  }, [pendingToggleKeys]);

  useEffect(() => {
    if (notifcationSettingsQuery.isFetched) {
      props.onLoad && props.onLoad();
    }
  }, [notifcationSettingsQuery.isFetched, props.onLoad]);

  const showInitialLoading = !notifications && (notifcationSettingsQuery.isLoading || notifcationSettingsQuery.isFetching);

  const setTogglePendingState = useCallback((toggleKey: string, isPending: boolean) => {
    setPendingToggleKeys((prev) => {
      if (isPending && prev[toggleKey]) {
        return prev;
      }

      if (!isPending && !prev[toggleKey]) {
        return prev;
      }

      const next = { ...prev };

      if (isPending) {
        next[toggleKey] = true;
      } else {
        delete next[toggleKey];
      }

      pendingToggleKeysRef.current = next;
      return next;
    });
  }, []);

  const updateNotificationState = useCallback((subject: MysteryShopperNotificationSubject, field: NotificationField, enabled: boolean) => {
    setNotifications((prev) => {
      if (!prev) {
        return prev;
      }

      const next = updateNotificationCollection(prev, subject, field, enabled);
      notificationsRef.current = next;
      return next;
    });
    queryClient.setQueryData(['notifcationSettingsQuery', userPrefs.uiLanguage], (prev: GetNotificationSettingsViewModel | undefined) => {
      if (!prev?.notificationSettings) {
        return prev;
      }

      return Object.assign(new GetNotificationSettingsViewModel(), prev, {
        notificationSettings: updateNotificationCollection(prev.notificationSettings, subject, field, enabled),
      });
    });
  }, [queryClient, userPrefs.uiLanguage]);

  const handleToggle = useCallback(
    (subject: MysteryShopperNotificationSubject | undefined, type: MysteryShopperNotificationType) => {
      if (subject === undefined) {
        return;
      }

      const toggleKey = getToggleKey(subject, type);
      if (pendingToggleKeysRef.current[toggleKey]) {
        return;
      }

      const item = notificationsRef.current?.find((notification) => notification.subject === subject);
      if (!item) {
        return;
      }

      const field: NotificationField = type === MysteryShopperNotificationType.Email ? 'emailEnabled' : 'pushNotificationEnabled';
      const nextEnabled = !(item[field] ?? false);

      updateNotificationState(subject, field, nextEnabled);
      setTogglePendingState(toggleKey, true);

      let data = new SaveNotificationSettingsRequest();
      data.subject = subject;
      data.type = type;
      data.enabled = nextEnabled;

      saveNotificationSettingMutation.mutate(data, {
        onError: () => {
          updateNotificationState(subject, field, !nextEnabled);
        },
        onSettled: () => {
          setTogglePendingState(toggleKey, false);
        },
      });
    },
    [saveNotificationSettingMutation, setTogglePendingState, updateNotificationState]
  );

  const handleEmailToggle = useCallback(
    (subject: MysteryShopperNotificationSubject | undefined) => {
      handleToggle(subject, MysteryShopperNotificationType.Email);
    },
    [handleToggle]
  );

  const handleMobileToggle = useCallback(
    (subject: MysteryShopperNotificationSubject | undefined) => {
      handleToggle(subject, MysteryShopperNotificationType.PushNotification);
    },
    [handleToggle]
  );

  const content = (
    <View>
      {!showInitialLoading ? (
        <>
          {notifications?.map((x, i) => {
            const subject = x.subject;
            const emailDisabled = subject !== undefined && pendingToggleKeys[getToggleKey(subject, MysteryShopperNotificationType.Email)] === true;
            const mobileDisabled = subject !== undefined && pendingToggleKeys[getToggleKey(subject, MysteryShopperNotificationType.PushNotification)] === true;

            return (
              <NotificationSettingRow
                key={x.subject ?? `notificationSetting_${i}`}
                item={x}
                titleColor={theme.text.titleDark.toString()}
                emailLabel={t('email')}
                mobileLabel={t('Mobile')}
                emailDisabled={emailDisabled}
                mobileDisabled={mobileDisabled}
                onEmailToggle={handleEmailToggle}
                onMobileToggle={handleMobileToggle}
              />
            );
          })}
        </>
      ) : (
        <Loading style={{ marginTop: 24 }} />
      )}
    </View>
  );

  if (props.noWrapper) {
    return content;
  }

  return (
    <MyAccountPageWrapper
      navigationTitle={t('settings')}
      title={t('Notifications')}
      navigationAction={NavigationElements.settings}
      titleStyle={{ textTransform: 'none', marginBottom: 20 }}
    >
      {content}
    </MyAccountPageWrapper>
  );
};

export default SettingsNotifications;
